import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { VariantFormValue } from '@shared/models/recipe/variant-form-value.model';
import { ApplianceGroup, DataRefsByKey, MarketingFood, Status, Unit, Utensil } from '@shared/models/dataref.model';
import { TextPopinComponent } from '@shared/components/text-popin/text-popin.component';
import { RecipeService } from '@services/recipe/recipe.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '@services/notification.service';

@Component({
  selector: 'app-variant-tab',
  templateUrl: './variant-tab.component.html',
  styleUrls: ['./variant-tab.component.scss'],
  standalone: false,
})
export class VariantTabComponent implements OnChanges, OnDestroy {
  @Input() variant?: RecipeLite | null = {} as RecipeLite;
  @Input() statusesRefData?: DataRefsByKey<Status> | null;
  @Input() yieldUnits?: DataRefsByKey<Unit> | null;
  @Input() marketingFoods: MarketingFood[] = [];
  @Input() applianceGroupsRefData?: DataRefsByKey<ApplianceGroup> | null;
  @Input() utensilsRefData?: DataRefsByKey<Utensil> | null;

  @Output() variantModified = new EventEmitter<boolean>();
  @Output() updateVariant = new EventEmitter<{ variant: RecipeLite; newValue: VariantFormValue }>();
  @Output() variantDeleted = new EventEmitter<void>();

  formVariant?: UntypedFormGroup;
  formStatusSubscription?: Subscription;

  private initiatedUpdate = false;

  constructor(
    public dialog: MatDialog,
    private readonly recipeService: RecipeService,
    private readonly notificationService: NotificationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.variant && !this.formVariant) {
      this.initFormGroup(this.variant);
    }

    if (changes.variant?.currentValue && this.initiatedUpdate) {
      this.formVariant?.markAsPristine();
      this.variantModified.emit(false);
      this.initiatedUpdate = false;
    }
  }

  initFormGroup(variant?: RecipeLite | null): void {
    if (!variant) {
      return;
    }

    const stepsList = variant.steps?.map((step) => RecipeService.buildFormGroupStep(step)) || [];

    // For each control, we start by testing if it exists and is pristine in order to reinitialize only what's needed.
    // This is necessary for performance concerns because we have a large form with a lot of components.
    this.formVariant = new UntypedFormGroup({
      status: this.formVariant?.controls.status.pristine
        ? this.formVariant.controls.status
        : new UntypedFormControl(variant.status, Validators.required),
      // Filled in variant-step-ingredients component initialization
      ingredients: this.formVariant?.controls.ingredients.pristine ? this.formVariant.controls.ingredients : new UntypedFormArray([]),
      applianceGroups: this.formVariant?.controls.applianceGroups.pristine
        ? this.formVariant.controls.applianceGroups
        : new UntypedFormControl(JSON.parse(JSON.stringify(variant.applianceGroups ?? [])) ?? []),
      defaultApplianceGroup: this.formVariant?.controls.defaultApplianceGroup.pristine
        ? this.formVariant.controls.defaultApplianceGroup
        : new UntypedFormControl(variant.defaultApplianceGroup),
      kitchenwares: this.formVariant?.controls.kitchenwares.pristine
        ? this.formVariant.controls.kitchenwares
        : new UntypedFormControl(variant.kitchenwares ?? []),
      steps: this.formVariant?.controls.steps.pristine ? this.formVariant.controls.steps : new UntypedFormArray(stepsList ?? []),
      utensils: this.formVariant?.controls.utensils.pristine
        ? this.formVariant.controls.utensils
        : new UntypedFormControl(variant.utensils ?? []),
      techniques: this.formVariant?.controls.techniques.pristine
        ? this.formVariant.controls.techniques
        : new UntypedFormControl(variant.techniques ?? []),
      durations: this.formVariant?.controls.durations.pristine
        ? this.formVariant.controls.durations
        : new UntypedFormControl(JSON.parse(JSON.stringify(variant.durations))),
      tips: this.formVariant?.controls.tips.pristine ? this.formVariant.controls.tips : new UntypedFormControl(variant.tips),
      summary: this.formVariant?.controls.summary.pristine ? this.formVariant.controls.summary : new UntypedFormControl(variant.summary),
      isAutomaticallyGenerated: this.formVariant?.controls.isAutomaticallyGenerated.pristine
        ? this.formVariant.controls.isAutomaticallyGenerated
        : new UntypedFormControl(variant.isAutomaticallyGenerated ?? false),
      applianceMinimumVolume: this.formVariant?.controls.applianceMinimumVolume.pristine
        ? this.formVariant.controls.applianceMinimumVolume
        : new UntypedFormControl(variant.applianceMinimumVolume),
      nutrition: this.formVariant?.controls.nutrition.pristine
        ? this.formVariant.controls.nutrition
        : new FormGroup({
          nutritionalIndicator: new FormControl(variant.nutrition?.nutritionalIndicator),
          energyValue: new FormControl(variant.nutrition?.energyValue),
          nutritionalScore: new FormControl(variant.nutrition?.nutritionalScore),
          weight: new FormControl(variant.nutrition?.weight),
          ecologicalScore: new FormControl(variant.nutrition?.ecologicalScore),
          hierarchicalPnnsPortions: new FormControl(variant.nutrition?.hierarchicalPnnsPortions),
          nutrients: new FormControl(variant.nutrition?.nutrients),
          areHPnnsPortionsManuallyModified: new FormControl(variant.nutrition?.areHPnnsPortionsManuallyModified),
          areNutritionalIndicatorManuallyModified: new FormControl(variant.nutrition?.areNutritionalIndicatorManuallyModified),
        }),
      detectedNutrition: this.formVariant?.controls.detectedNutrition.pristine
        ? this.formVariant.controls.detectedNutrition
        : new FormGroup({
          empty: new FormControl(variant.detectedNutrition?.empty ?? false),
          nutritionalIndicator: new FormControl(variant.detectedNutrition?.nutritionalIndicator),
          detectedEnergyValue: new FormControl(variant.detectedNutrition?.detectedEnergyValue),
          nutritionalScore: new FormControl(variant.detectedNutrition?.nutritionalScore),
          weight: new FormControl(variant.detectedNutrition?.weight),
          ecologicalScore: new FormControl(variant.detectedNutrition?.ecologicalScore),
        }),
    });

    this.formStatusSubscription?.unsubscribe();

    this.formStatusSubscription = this.formVariant.statusChanges.subscribe(() => {
      this.variantModified.emit(this.formVariant?.dirty ?? false);
    });

    this.variantModified.emit(false);
  }

  ngOnDestroy(): void {
    this.formStatusSubscription?.unsubscribe();
  }

  saveVariant(): void {
    if (this.variant && this.formVariant) {
      this.initiatedUpdate = true;
      this.updateVariant.emit({
        variant: this.variant,
        newValue: this.formVariant.value,
      });
    }
  }

  deleteVariant(): void {
    if (this.variant && this.formVariant) {
      const dialogRef = this.dialog.open(TextPopinComponent, {
        data: {
          title: 'RECIPE_DELETE_MODAL_TITLE',
          text: 'RECIPE_DELETE_MODAL_TEXT',
          cancelBtn: 'NO',
          validateBtn: 'YES',
        },
      });
      dialogRef.afterClosed().subscribe((userHasValidated: boolean | undefined) => {
        if (userHasValidated) {
          if (this.variant) {
            this.recipeService
              .deleteRecipe(encodeURIComponent(this.variant?.identifier.functionalId), this.variant?.identifier.sourceSystem)
              .subscribe(
                () => {
                  this.notificationService.sendSuccess('DELETE_RECIPES.MODAL.MSG.SUCCESS_ONE');
                  this.variantDeleted.emit();
                },
                (error) => {
                  this.notificationService.sendAPIError(error);
                },
              );
          }
        }
      });
    }
  }
}
